import * as React from 'react'
import { ChevronDown, Check, Search } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface DropdownOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
  searchableText?: string // Optional text for search if label is complex
}

interface DropdownProps {
  options: DropdownOption[]
  variant?: 'default' | 'short'
  value?: string
  placeholder?: string
  onValueChange: (value: string) => void
  className?: string
  disabled?: boolean
  align?: 'start' | 'center' | 'end'
  searchable?: boolean
  searchPlaceholder?: string
  maxHeight?: string
}

export const Dropdown = ({
  options,
  value,
  placeholder = 'Select option...',
  onValueChange,
  className,
  disabled = false,
  align = 'start',
  variant = 'default',
  searchable = false,
  searchPlaceholder = 'Search...',
  maxHeight = '400px'
}: DropdownProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close dropdown on escape key
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  const selectedOption = options.find((option) => option.value === value)

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchable || !searchTerm) return options

    return options.filter((option) => {
      const searchText =
        option.searchableText || (typeof option.label === 'string' ? option.label : option.value)
      return searchText.toLowerCase().includes(searchTerm.toLowerCase())
    })
  }, [options, searchTerm, searchable])

  const handleOptionClick = (optionValue: string) => {
    onValueChange(optionValue)
    setIsOpen(false)
  }

  const getAlignmentClasses = () => {
    switch (align) {
      case 'center':
        return 'left-1/2 -translate-x-1/2'
      case 'end':
        return 'right-0'
      default:
        return 'left-0'
    }
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          variant === 'default'
            ? 'flex w-full items-center justify-between px-3 py-2'
            : 'flex h-8 w-full items-center justify-between px-2',
          'bg-background dark:bg-input/30 border-input rounded-md border',
          'text-left text-sm font-normal shadow-sm dark:shadow-none',
          'transition-all duration-200',
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-input/50',
          'hover:shadow-md dark:hover:shadow-none',
          'focus:ring-ring focus:ring-2 focus:ring-offset-2 focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          !disabled && 'cursor-pointer',
          className
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'ml-2 h-4 w-4 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => setIsOpen(false)} />

          {/* Dropdown Menu */}
          <div
            className={cn(
              'absolute top-full mt-1 w-full max-w-sm sm:min-w-[200px]',
              'bg-popover border-border rounded-md border shadow-lg dark:shadow-none',
              'shadow-[0_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0_10px_20px_-15px_rgba(22,_23,_24,_0.2)]',
              'z-50 overflow-hidden',
              'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              getAlignmentClasses()
            )}
            role="listbox"
            style={{ maxHeight }}
          >
            {/* Search Input */}
            {searchable && (
              <div className="border-border border-b p-2">
                <div className="relative">
                  <Search className="text-muted-foreground absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={cn(
                      'flex h-8 w-full rounded-sm border border-transparent bg-transparent pr-3 pl-8 text-base',
                      'placeholder:text-muted-foreground',
                      'focus:border-ring focus:outline-none',
                      'disabled:cursor-not-allowed disabled:opacity-50'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            {/* Options List */}
            <div
              className="overflow-auto p-1"
              style={{ maxHeight: searchable ? `calc(${maxHeight} - 60px)` : maxHeight }}
            >
              {filteredOptions.length === 0 && searchTerm ? (
                <div className="text-muted-foreground px-2 py-1.5 text-center text-sm">
                  No results found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => !option.disabled && handleOptionClick(option.value)}
                    disabled={option.disabled}
                    className={cn(
                      'flex w-full items-center justify-between px-2 py-1.5',
                      'rounded-sm text-left text-sm',
                      'transition-colors duration-150',
                      'hover:bg-accent hover:text-accent-foreground',
                      'focus:bg-accent focus:text-accent-foreground focus:outline-none',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      !option.disabled && 'cursor-pointer',
                      value === option.value && 'bg-accent text-accent-foreground'
                    )}
                    role="option"
                    aria-selected={value === option.value}
                  >
                    <span className="truncate">{option.label}</span>
                    {value === option.value && (
                      <Check className="text-primary ml-2 h-4 w-4 flex-shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
